import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  Feature,
  SeatSaverDefaultSettings,
  SellerAccountClient,
} from 'src/WebApiController';

export const useSeatSaverDefaultSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasFeature = useUserHasFeature(Feature.SeatSaver);

  const queryKey = [
    'SellerAccountClient.getSeatSaverDefaultSettings',
    activeAccountWebClientConfig.activeAccountId,
    hasFeature,
  ];

  const shouldQuery =
    hasFeature && activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) return null;
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getSeatSaverDefaultSettings();
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (settings: SeatSaverDefaultSettings) => {
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergeSeatSaverDefaultSettings(settings);
    },
    onError: (err: ErrorTypes, settings) => {
      showErrorDialog(
        'SellerAccountClient.mergeSeatSaverDefaultSettings',
        err,
        {
          trackErrorData: { settings },
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    mutation,
  };
};
